import { FC, useMemo, useState } from 'react'
import { Form, Navigate, redirect, useParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'

// COMPONENTS
import { Column } from '@components/templates/column'
import * as Forms from '@components/forms'
import Button from '@components/atoms/button'
import CharacterCount from '@components/atoms/character-count'
import DropDownMenu from '@components/atoms/drop-down-menu'
import PaymentMethodCell from '@components/organisims/payment-method-cell'
import TextField from '@components/atoms/textfield'

// TYPES
import type { Address } from '@stripe/stripe-js'
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { StripeAPI } from '@apis'
import { createAction } from '@utils/actions'
import { isNonEmptyString } from '@utils/type-checkers'
import { usePaymentMethods } from '@/hooks/tanstack/usePaymentMethods'
import { useInput } from '@hooks/inputs'
import { queryClient } from '@/lib/queryClient'


type OnAddressChangeParams = Parameters<NonNullable<React.ComponentProps<typeof Forms.BillingAddressFields>['onAddressChange']>>

const formId = "edit-pm-form"
const ccNameMax = 25
const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]
const years = Array.from({ length: 26 }, (_, i) => (new Date().getFullYear() + i).toString())

const Component: FC<{}> = () => {
    const { user } = useUser()
    const { token } = useParams()
    const { data: paymentMethods = [], isPending } = usePaymentMethods(user?.id)
    const method = useMemo(() => (paymentMethods.find((method) => (method.token === token))), [paymentMethods, token])

    const [expMonth, setExpMonth] = useState(method?.expirationMonth || '')
    const [expYear, setExpYear] = useState(`20${method?.expirationYear || ''}`)

    const [billingAddress, setBillingAddress] = useState<{ address?: Address, valid?: boolean }>({ address: method?.address, valid: !!method?.address })
    const [ccName, ccNameProps] = useInput({ initValue: method?.name || '' })

    function handleAddressChange(...[address, valid]: OnAddressChangeParams) {
        setBillingAddress({ address, valid })
    }

    if (isPending) return null
    if (!method || !token) return <Navigate to="/account/pm" replace />
    else return (<>
        <Form id={formId} className="flex flex-col" method="POST" style={{ gap: 'inherit' }}>
            <input name="expMonth" value={expMonth} readOnly hidden />
            <input name="expYear" value={expYear} readOnly hidden />
            <PaymentMethodCell id='edit' paymentMethod={{ ...method, expirationMonth: '', expirationYear: '' }} style={{ cursor: 'default' }}>
                <p><strong>Expiration Date</strong></p>
                <div className='flex flex-row gap-4'>
                    <DropDownMenu setActiveItem={setExpMonth} activeItem={expMonth} items={months} />
                    <DropDownMenu setActiveItem={setExpYear} activeItem={expYear} items={years} />
                </div>
                <p><strong>Nickname (Optional)</strong></p>
                <TextField {...ccNameProps} name="cc-name" maxLength={ccNameMax} placeholder="&quot;Enter a nickname for this card&quot;" autoComplete="off" inlineElementRight={<CharacterCount label={ccName} max={ccNameMax} />} />
            </PaymentMethodCell>
            <h3 style={{ textAlign: 'center' }}>Billing Address</h3>
            <Forms.BillingAddressFields initAddress={method.address} onAddressChange={handleAddressChange} name="billing-address" />
        </Form>
        <Column.Footer>
            <Button form={formId} type="submit" disabled={(!billingAddress.valid)}>Finish</Button>
        </Column.Footer>
    </>)
}

const action = createAction(async ({ request, params }) => {
    const { token } = params
    const formData = await request.formData()

    const name = parseData('cc-name', formData)
    const expirationMonth = parseData('expMonth', formData)
    const expirationYear = parseData('expYear', formData)?.replace(/20/, "")
    const address: Address = {
        line1: parseData('line1', formData) || null,
        line2: parseData('line2', formData) || null,
        city: parseData('city', formData) || null,
        state: parseData('state', formData) || null,
        postal_code: parseData('postal_code', formData) || null,
        country: parseData('country', formData) || null,
    }

    console.log("Update PM Data:", { token, name, expirationMonth, expirationYear, address })
    if (!isNonEmptyString(token)
        || (isNonEmptyString(name) ? name!.length > ccNameMax : false)
        || (!isNonEmptyString(expirationMonth) || !isNonEmptyString(expirationYear))
        || !(
            isNonEmptyString(address.line1)
            && isNonEmptyString(address.city)
            && isNonEmptyString(address.state)
            && isNonEmptyString(address.postal_code)
            && isNonEmptyString(address.country)
        )
    ) throw new Error(`Missing required resources. ${JSON.stringify({ token, name, expirationMonth, expirationYear, address })}`)

    await StripeAPI.UpdatePaymentMethodRequest.fetch(token, { name, expirationMonth, expirationYear, address })
    queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
    return redirect('../')
})

const parseData = (key: string, formData: FormData) => `${formData.get(key) || ''}` || undefined

const EditPMRoute: RouteObject = {
    path: ':token/edit',
    Component,
    action,
}

export default EditPMRoute
